import { useMemo, useState } from 'react';

import { groupBy, orderBy, uniqBy } from 'lodash';

import {
  AddEntertainerToDragKingCommunitySpotlightsPayload,
  CreateBookDragKingsQuotePayload,
  ScheduleEntertainerQuotePayload,
  UpdateBookDragKingsQuotePayload,
  useAddEntertainerToDragKingCommunitySpotlightsMutation,
  useBookDragKingsQuotesQuery,
  useCreateBookDragKingsEntertainerQuoteMutation,
  useScheduleBookDragKingsEntertainerQuoteMutation,
  useUpdateBookDragKingsEntertainerQuoteMutation,
} from '@admin/queries/AdminEntertainerQuotesQueries';

import { getApiUrl } from '@api/API';

import { Select } from '@components/form/Select';
import Card from '@components/layout/Card/Card';

import { useSortEntertainerQuotes } from '@screens/super-admin/SuperAdminManageCDGQuotesScreen';
import { EntertainerQuotesSection } from '@screens/super-admin/components/EntertainerQuotesSection';

export const ManageShoutouts = () => {
  const {
    bookDragKingsQuotesAreReady,
    bookDragKingsQuotes = [],
    refetchBookDragKingsQuotes,
  } = useBookDragKingsQuotesQuery();
  const [cityId, setCityId] = useState<string | undefined>(undefined);

  const { createQuoteAsync } = useCreateBookDragKingsEntertainerQuoteMutation();
  const { updateQuoteAsync } = useUpdateBookDragKingsEntertainerQuoteMutation();
  const { scheduleQuoteAsync } = useScheduleBookDragKingsEntertainerQuoteMutation();
  const { isAddingEntertainerInstagramHandle, addEntertainerInstagramHandleAsync } =
    useAddEntertainerToDragKingCommunitySpotlightsMutation();

  const entertainerCountsByCityId = useMemo(
    () =>
      groupBy(
        bookDragKingsQuotes.flatMap((entertainerQuotes) =>
          entertainerQuotes.entertainer.cityIds.map((cityId) => ({
            entertainerQuotes,
            cityId,
          })),
        ),
        'cityId',
      ),
    [bookDragKingsQuotes],
  );

  const cityOptions = useMemo(() => {
    return [
      { value: '', label: 'All Cities', entertainerCount: bookDragKingsQuotes.length },
      ...uniqBy(
        bookDragKingsQuotes
          .map((entertainerQuotes) =>
            entertainerQuotes.entertainer.cities.map((city) => ({
              value: city._id,
              label: city.name,
            })),
          )
          .flat(),
        'value',
      )
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((city) => {
          const entertainerCount = entertainerCountsByCityId[city.value]?.length ?? 0;

          return {
            value: city.value,
            label: `${city.label} (${entertainerCount} ${entertainerCount === 1 ? 'entertainer' : 'entertainers'})`,
            entertainerCount,
          };
        }),
    ];
  }, [bookDragKingsQuotes, entertainerCountsByCityId]);

  const sortedCityOptions = useMemo(() => {
    return cityOptions
      .filter((city) => city.value !== '')
      .sort((a, b) => b.entertainerCount - a.entertainerCount);
  }, [cityOptions]);

  // const sortedQuotes = useMemo(
  //   () =>
  //     orderBy(
  //       bookDragKingsQuotes,
  //       [
  //         ({ entertainerFollowerCount }) => entertainerFollowerCount,
  //         ({ quotesAboutEntertainer }) => quotesAboutEntertainer.length,
  //         ({ entertainer }) => entertainer.title,
  //       ],
  //       ['desc', 'desc', 'asc'],
  //     ),
  //   [bookDragKingsQuotes],
  // );

  const {
    sortedEntertainerQuotes,
    entertainerQuotesOrderOptions,
    orderEntertainerBy,
    updateOrderEntertainerBy,
  } = useSortEntertainerQuotes(bookDragKingsQuotes);

  const filteredQuotes = useMemo(() => {
    if (!cityId) {
      return sortedEntertainerQuotes;
    }

    return sortedEntertainerQuotes.filter((quote) =>
      quote.entertainer.cities.some((city) => city._id === cityId),
    );
  }, [sortedEntertainerQuotes, cityId]);

  const handleCreateQuote = async (payload: CreateBookDragKingsQuotePayload) => {
    const response = await createQuoteAsync(payload);
    refetchBookDragKingsQuotes();

    return response;
  };

  const handleUpdateQuote = async (payload: UpdateBookDragKingsQuotePayload) => {
    await updateQuoteAsync(payload);
    refetchBookDragKingsQuotes();
  };

  const handleScheduleQuote = async (payload: ScheduleEntertainerQuotePayload) => {
    await scheduleQuoteAsync(payload);
    refetchBookDragKingsQuotes();
  };

  const handleAddEntertainerInstagramHandle = async (
    payload: AddEntertainerToDragKingCommunitySpotlightsPayload,
  ) => {
    await addEntertainerInstagramHandleAsync(payload);
    refetchBookDragKingsQuotes();
  };

  const getPreviewUrl = ({ quoteId, quoteFontSize }: { quoteId: string; quoteFontSize: number }) =>
    `${getApiUrl()}/v1/book-drag-kings-quotes/${quoteId}/quote.jpg?quoteFontSize=${quoteFontSize}`;

  const scheduledQuotes = useMemo(() => {
    return bookDragKingsQuotes.filter((quote) =>
      quote.quotesAboutEntertainer.some(
        (quoteAboutEntertainer) => quoteAboutEntertainer.curatedQuote?.status === 'scheduled',
      ),
    );
  }, [bookDragKingsQuotes]);

  if (!bookDragKingsQuotesAreReady) {
    return <div>Loading...</div>;
  }

  if (!bookDragKingsQuotes?.length) {
    return <div>No quotes found.</div>;
  }
  return (
    <div>
      <div className="mb-4">
        <Card>
          <Card.Body>
            <div>
              <div className="mb-2 d-flex justify-content-between align-items-center">
                <div>
                  <strong>Top Cities</strong>
                </div>
                <div>{`${scheduledQuotes.length} scheduled`}</div>
              </div>
              <div>
                {sortedCityOptions.slice(0, 10).map((city, index) => (
                  <div key={city.value}>
                    <div>
                      <strong>{index + 1}.</strong> {city.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
      <div className="mb-4 d-grid gap-2">
        <Select options={cityOptions} value={cityId} onChange={(e) => setCityId(e.target.value)} />

        <Select
          options={entertainerQuotesOrderOptions}
          value={orderEntertainerBy}
          onChange={(e) => updateOrderEntertainerBy(e.target.value as any)}
        />
      </div>
      {filteredQuotes.map((entertainerQuotes) => (
        <EntertainerQuotesSection
          key={entertainerQuotes.entertainer._id ?? entertainerQuotes.entertainer.title}
          entertainerQuotes={entertainerQuotes}
          previewUrl={getPreviewUrl}
          onCreate={handleCreateQuote}
          onUpdate={handleUpdateQuote}
          onSchedule={handleScheduleQuote}
          onAddEntertainerInstagramHandle={handleAddEntertainerInstagramHandle}
          isAddingEntertainerInstagramHandle={isAddingEntertainerInstagramHandle}
          allowCarousel
        />
      ))}
    </div>
  );
};
